/**
 * 合同台账（延伸需求第一部分）
 *
 * 合同全生命周期的入口页：层级、执行进度、审批状态、预警。
 *
 * 【为什么把"执行进度"直接摆在台账上】剩余可开票额度与剩余应付是财务每天要看的两件事。
 * 让它们出现在列表里（而不是点进详情再算），财务才不会靠心算——
 * 而心算错一次就是一笔超开票或超付款。
 *
 * 【口径】列表里的「已开票 / 已付款 / 剩余」全部是**服务端读时聚合**的结果，
 * 前端不做任何加减。前端各算一份的话，台账与详情迟早对不上。
 */

import { useMemo, useState } from 'react';
import { Button, Card, Col, DatePicker, Form, Input, InputNumber, Modal, Row, Select, Space, Tag, Alert, Tooltip } from 'antd';
import { PlusOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  ContractStatus,
  ContractType,
  CONTRACT_STATUS_LABELS,
  CONTRACT_TYPE_LABELS,
  CounterpartyRole,
  COUNTERPARTY_ROLE_LABELS,
  PermissionCode,
  SettlementMethod,
  SETTLEMENT_METHOD_LABELS,
} from '@ilc/shared';
import type { Contract } from '@ilc/shared';
import { contractApi } from '@/api/endpoints';
import { usePagedList } from '@/hooks/usePagedList';
import { useAuthStore } from '@/stores/auth';
import { DataTable } from '@/components/DataTable';
import { PageHeader } from '@/components/PageHeader';
import { Money, moneyCellStyle } from '@/components/Money';
import { MaskedField } from '@/components/MaskedField';
import { ConfirmDanger } from '@/components/ConfirmDanger';
import { ContractAttachmentIntake } from '@/components/ContractAttachmentIntake';
import { feedback } from '@/utils/feedback';
import { performAction } from '@/utils/performAction';
import { exportMoney } from '@/utils/export';

interface Query {
  page?: number;
  pageSize?: number;
  keyword?: string;
  contractType?: string;
  level?: string;
  status?: string;
  hasAlert?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/** 品类归一：数组原样用；字符串按顿号/逗号拆。空值一律 null（不传空数组给后端） */
function normalizeCategories(raw: unknown): string[] | null {
  const list = Array.isArray(raw)
    ? raw
    : typeof raw === 'string'
      ? raw.split(/[、,，]/)
      : [];
  const cleaned = list.map((x) => String(x).trim()).filter(Boolean);
  return cleaned.length > 0 ? cleaned : null;
}

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'default',
  APPROVING: 'processing',
  ACTIVE: 'success',
  REJECTED: 'warning',
  TERMINATED: 'error',
  ARCHIVED: 'default',
};

export function ContractLedgerPage(): React.ReactElement {
  const can = useAuthStore((s) => s.can);
  const navigate = useNavigate();
  const canWrite = can(PermissionCode.CONTRACT_WRITE);
  const canExport = can(PermissionCode.CONTRACT_EXPORT);

  const list = usePagedList<Contract, Query>({
    loader: (q) => contractApi.list(q),
    initialQuery: { page: 1, pageSize: 20, status: ContractStatus.ACTIVE },
  });

  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  /**
   * 本次上传但还没绑定到合同的附件（TEMP 态）。
   *
   * 「先传合同电子版 → 自动识别 → 核对后保存」这条路径要求附件先于合同存在，
   * 保存时再一并绑定。有任何一个绑不上后端会整体报 400，**不做静默的部分绑定**
   * ——那会让用户以为附件都挂上了。
   */
  const [tempAttachmentIds, setTempAttachmentIds] = useState<string[]>([]);
  const [submitTarget, setSubmitTarget] = useState<Contract | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<Contract | null>(null);

  /**
   * 编辑弹窗的回填用 `initialValues` + `key`，**不用 setFieldsValue**。
   * 这里是新建（无回填）但规则统一：`key` 变化即整体重建。
   */
  const formInitialValues = useMemo(() => ({ contractType: ContractType.PURCHASE, counterpartyRole: CounterpartyRole.CONTRACTOR, amountYuan: undefined as number | undefined }), []);

  /** 台账合计。用当前页数据——**页面底部标明"本页合计"**，不冒充全量 */
  const totals = useMemo(() => {
    const rows = list.list;
    return {
      amount: rows.reduce((s, r) => s + (r.amountCents ?? 0), 0),
      invoiced: rows.reduce((s, r) => s + (r.invoicedCents ?? 0), 0),
      remaining: rows.reduce((s, r) => s + (r.remainingCents ?? 0), 0),
      paid: rows.reduce((s, r) => s + (r.paidCents ?? 0), 0),
      payable: rows.reduce((s, r) => s + (r.payableCents ?? 0), 0),
    };
  }, [list.list]);

  const columns = [
    {
      title: '合同编号',
      dataIndex: 'contractNo',
      width: 150,
      fixed: 'left' as const,
      render: (v: string, r: Contract) => <Link to={`/business/contracts/${r.id}`} className="ilc-mono">{v}</Link>,
    },
    {
      title: '合同名称',
      dataIndex: 'name',
      width: 240,
      ellipsis: true,
      render: (v: string, r: Contract) => (
        <span>
          {r.level === 'L2' && <Tag color="blue">L2</Tag>}
          {r.level === 'L3' && <Tag color="purple">L3</Tag>}
          <Link to={`/business/contracts/${r.id}`}>{v}</Link>
        </span>
      ),
    },
    {
      title: '类型',
      dataIndex: 'contractType',
      width: 96,
      render: (v: ContractType) => CONTRACT_TYPE_LABELS[v] ?? v,
    },
    {
      title: '对方单位',
      dataIndex: 'supplierName',
      width: 200,
      ellipsis: true,
      render: (v: string, r: Contract) => (
        <Space size={4}>
          <span>{v}</span>
          {r.counterpartyRole === CounterpartyRole.OWNER && <Tag>甲方</Tag>}
        </Space>
      ),
    },
    {
      title: '税号',
      dataIndex: 'supplierTaxNo',
      width: 170,
      render: (v: string | null) => (
        <MaskedField value={v} kind="taxNo" permission={PermissionCode.CONTRACT_READ} />
      ),
    },
    {
      title: '合同金额',
      dataIndex: 'amountCents',
      width: 130,
      align: 'right' as const,
      onCell: () => ({ style: moneyCellStyle }),
      render: (v: number) => <Money cents={v} strong />,
    },
    {
      title: '已开票',
      dataIndex: 'invoicedCents',
      width: 130,
      align: 'right' as const,
      onCell: () => ({ style: moneyCellStyle }),
      render: (v: number, r: Contract) => (
        <Tooltip title={`剩余可开票 ${exportMoney(r.remainingCents)}`}>
          <span><Money cents={v} /></span>
        </Tooltip>
      ),
    },
    {
      title: '剩余可开票',
      dataIndex: 'remainingCents',
      width: 140,
      align: 'right' as const,
      onCell: () => ({ style: moneyCellStyle }),
      render: (v: number, r: Contract) => (
        // 剩余为 0 说明额度已用尽，标出来——这是财务最该注意的一行
        <span style={{ color: v === 0 && r.amountCents > 0 ? '#cf1322' : undefined }}>
          <Money cents={v} />
        </span>
      ),
    },
    {
      title: '已付款',
      dataIndex: 'paidCents',
      width: 130,
      align: 'right' as const,
      onCell: () => ({ style: moneyCellStyle }),
      render: (v: number) => <Money cents={v} />,
    },
    {
      title: '剩余应付',
      dataIndex: 'payableCents',
      width: 130,
      align: 'right' as const,
      onCell: () => ({ style: moneyCellStyle }),
      render: (v: number) => <Money cents={v} />,
    },
    {
      title: '工期',
      dataIndex: 'endDate',
      width: 180,
      render: (_: unknown, r: Contract) =>
        r.startDate || r.endDate ? (
          <span className="ilc-mono" style={{ fontSize: 12 }}>
            {r.startDate ?? '—'} ~ {r.endDate ?? '—'}
          </span>
        ) : (
          // 缺失渲染成「-」，不是空白也不是 0：0 的含义是"确实没有"，与"没填"不同
          <span style={{ color: 'rgba(0,0,0,0.35)' }}>-</span>
        ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 96,
      render: (v: ContractStatus) => <Tag color={STATUS_COLORS[v]}>{CONTRACT_STATUS_LABELS[v] ?? v}</Tag>,
    },
    {
      title: '预警',
      dataIndex: 'openAlertCount',
      width: 76,
      render: (v: number) =>
        v > 0 ? (
          <Link to="/business/contract-alerts">
            <Tag icon={<WarningOutlined />} color="error">{v}</Tag>
          </Link>
        ) : (
          <span style={{ color: 'rgba(0,0,0,0.35)' }}>-</span>
        ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      fixed: 'right' as const,
      render: (_: unknown, r: Contract) => (
        <Space size={4}>
          <Button size="small" type="link" onClick={() => navigate(`/business/contracts/${r.id}`)}>
            查看
          </Button>
          {canWrite && (r.status === ContractStatus.DRAFT || r.status === ContractStatus.REJECTED) && (
            <Button size="small" type="link" onClick={() => setSubmitTarget(r)}>
              提交审批
            </Button>
          )}
          {canWrite && r.status === ContractStatus.ACTIVE && (
            <Button size="small" type="link" onClick={() => setArchiveTarget(r)}>
              归档
            </Button>
          )}
          {r.status === ContractStatus.ARCHIVED && (
            <span style={{ color: 'rgba(0,0,0,0.35)', fontSize: 12 }}>已归档</span>
          )}
        </Space>
      ),
    },
  ];

  const save = async (): Promise<void> => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const result = await performAction({
        action: () =>
          contractApi.create({
            contractNo: values.contractNo || undefined,
            name: values.name,
            contractType: values.contractType,
            counterpartyRole: values.counterpartyRole,
            supplierName: values.supplierName,
            supplierTaxNo: values.supplierTaxNo || null,
            // 表单里填「元」，接口收「分」——转换只在这一次发生
            amountCents: Math.round(Number(values.amountYuan ?? 0) * 100),
            amountExclTaxCents: values.amountExclTaxYuan
              ? Math.round(Number(values.amountExclTaxYuan) * 100)
              : 0,
            taxCents: values.taxYuan ? Math.round(Number(values.taxYuan) * 100) : 0,
            settlementMethod: values.settlementMethod ?? null,
            signDate: values.signDates?.[0] ? dayjs(values.signDates[0]).format('YYYY-MM-DD') : null,
            startDate: values.period?.[0] ? dayjs(values.period[0]).format('YYYY-MM-DD') : null,
            endDate: values.period?.[1] ? dayjs(values.period[1]).format('YYYY-MM-DD') : null,
            settlementStage: values.settlementStage || null,
            /*
             * 品类归一：本页控件是 tags-Select（数组），但预填、手工输入、粘贴
             * 都可能给出顿号分隔的字符串。**接口要的是数组**，不归一就会 400
             * ——而那个 400 只在点保存时出现，面板与输入框看起来都正常。
             */
            categories: normalizeCategories(values.categories),
            remark: values.remark || null,
            // 本次上传但还没归属的附件一并绑定；没有则不传
            ...(tempAttachmentIds.length > 0 ? { attachmentIds: tempAttachmentIds } : {}),
          }),
        successMessage: '合同已登记',
        offline: () => undefined,
        offlineMessage: '后端合同接口尚未就绪，本次登记仅在本地演示，未写入数据库',
      });
      if (!result.ok) return;
      setCreating(false);
      form.resetFields();
      setTempAttachmentIds([]);
      list.reload();
    } finally {
      setSaving(false);
    }
  };

  const doSubmit = async (reason: string): Promise<void> => {
    if (!submitTarget) return;
    const result = await performAction({
      action: () => contractApi.submit(submitTarget.id),
      successMessage: `合同 ${submitTarget.contractNo} 已提交审批`,
      offline: () => undefined,
      offlineMessage: '后端提交流程接口尚未就绪，本次提交仅在本地演示',
    });
    if (result.ok) {
      feedback.info(reason ? `已提交审批（说明：${reason}）` : '已提交审批');
      setSubmitTarget(null);
      list.reload();
    }
  };

  const doArchive = async (reason: string): Promise<void> => {
    if (!archiveTarget) return;
    const result = await performAction({
      action: () => contractApi.archive(archiveTarget.id),
      successMessage: `合同 ${archiveTarget.contractNo} 已归档`,
      offline: () => undefined,
      offlineMessage: '后端归档接口尚未就绪，本次归档仅在本地演示',
    });
    if (result.ok) {
      feedback.info(`归档说明：${reason}`);
      setArchiveTarget(null);
      list.reload();
    }
  };

  return (
    <div className="ilc-page">
      <PageHeader
        title="合同台账"
        description="总包 / 分包 / 采购 / 租赁 / 服务合同与补充协议的全生命周期视图"
        extra={
          <Space wrap>
            {canWrite && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
                登记合同
              </Button>
            )}
            {canExport && (
              <Button
                onClick={() => {
                  // 后端返回 CSV 流；这里开新窗口下载。导出动作在后端已写审计
                  window.open(contractApi.exportUrl({ ...list.query }), '_blank');
                }}
              >
                导出当前筛选
              </Button>
            )}
            <Button icon={<ReloadOutlined />} onClick={list.reload}>
              刷新
            </Button>
          </Space>
        }
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="剩余可开票额度 = 合同金额 − 该合同下全部发票价税合计（实时汇总，不依赖发票走到哪一步）"
        description={
          <span>
            合同金额的变更必须走「变更 / 补充协议」并经过审批——在编辑里直接改金额会被拒绝。
            这样「为什么变成这个数」才留得下判据。
          </span>
        }
      />

      <DataTable<Contract>
        storageKey="contract-ledger"
        exportFileName="合同台账"
        exportColumns={[
          { title: '合同编号', dataIndex: 'contractNo' },
          { title: '合同名称', dataIndex: 'name' },
          { title: '类型', value: (r) => CONTRACT_TYPE_LABELS[r.contractType] ?? r.contractType },
          { title: '对方单位', dataIndex: 'supplierName' },
          { title: '合同金额(元)', value: (r) => exportMoney(r.amountCents) },
          { title: '已开票(元)', value: (r) => exportMoney(r.invoicedCents) },
          { title: '剩余可开票(元)', value: (r) => exportMoney(r.remainingCents) },
          { title: '已付款(元)', value: (r) => exportMoney(r.paidCents) },
          { title: '剩余应付(元)', value: (r) => exportMoney(r.payableCents) },
          { title: '状态', value: (r) => CONTRACT_STATUS_LABELS[r.status] ?? r.status },
        ]}
        columns={columns}
        dataSource={list.list}
        rowKey="id"
        loading={list.loading}
        error={list.error}
        isFallback={list.isFallback}
        fallbackReason={list.fallbackReason}
        onRetry={list.reload}
        pagination={list.pagination}
        onChange={list.handleTableChange}
        scrollX={1800}
        emptyText="还没有合同。点右上角「登记合同」开始。"
        emptyAction={canWrite ? <Button type="primary" onClick={() => setCreating(true)}>登记合同</Button> : null}
        toolbar={
          <Space wrap>
            <Input.Search
              allowClear
              placeholder="编号 / 名称 / 对方单位"
              style={{ width: 240 }}
              onSearch={(v) => list.setQuery({ keyword: v || undefined })}
            />
            <Select
              allowClear
              placeholder="合同类型"
              style={{ width: 140 }}
              value={list.query.contractType}
              onChange={(v) => list.setQuery({ contractType: v })}
              options={Object.entries(CONTRACT_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
            />
            <Select
              allowClear
              placeholder="层级"
              style={{ width: 110 }}
              value={list.query.level}
              onChange={(v) => list.setQuery({ level: v })}
              options={[
                { value: 'L1', label: '一级（总包）' },
                { value: 'L2', label: '二级（分包等）' },
                { value: 'L3', label: '三级（补充协议）' },
              ]}
            />
            <Select
              allowClear
              placeholder="状态"
              style={{ width: 120 }}
              value={list.query.status}
              onChange={(v) => list.setQuery({ status: v })}
              options={Object.entries(CONTRACT_STATUS_LABELS).map(([value, label]) => ({ value, label }))}
            />
            <Select
              allowClear
              placeholder="只看有预警"
              style={{ width: 130 }}
              value={list.query.hasAlert}
              onChange={(v) => list.setQuery({ hasAlert: v })}
              options={[{ value: 'true', label: '有未处理预警' }]}
            />
            <Button onClick={list.resetQuery}>重置</Button>
          </Space>
        }
        footer={() =>
          // 用 footer 而不是 antd 的 Summary：DataTable 只透传 columns/pagination 这些，
          // 自己拼一行合计更可控，也避免为一个数字引入新的表格 API
          list.list.length > 0 ? (
            <Space size={24} wrap style={{ fontSize: 13 }}>
              <span>
                <strong>本页合计（共 {list.list.length} 条）：</strong>
              </span>
              <span>
                合同金额 <Money cents={totals.amount} strong />
              </span>
              <span>
                已开票 <Money cents={totals.invoiced} />
              </span>
              <span>
                剩余可开票 <Money cents={totals.remaining} />
              </span>
              <span>
                已付款 <Money cents={totals.paid} />
              </span>
              <span>
                剩余应付 <Money cents={totals.payable} />
              </span>
            </Space>
          ) : null
        }
      />

      <Card size="small" title="字段口径" style={{ marginTop: 16 }}>
        <Row gutter={[16, 8]}>
          <Col xs={24} md={12}>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
              <li><strong>剩余可开票</strong>：合同金额 − 已开票。发票**开出即占用额度**，与本平台流程走到哪一步无关。</li>
              <li><strong>剩余应付</strong>：合同金额 − <em>已付款</em>（只算状态为「已付款」的付款记录；申请中与已作废的不算）。</li>
            </ul>
          </Col>
          <Col xs={24} md={12}>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
              <li><strong>层级</strong>：由合同类型推导（总包=一级、补充协议=三级、其余=二级），不是单独填的字段。</li>
              <li><strong>归档</strong>：审批通过只是<em>生效</em>；归档是项目结束时执行人主动做的动作，且要求先处理完未处理预警。</li>
            </ul>
          </Col>
        </Row>
      </Card>

      {/* —— 登记弹窗 —— */}
      <Modal
        open={creating}
        title="登记合同"
        width={860}
        onCancel={() => setCreating(false)}
        onOk={() => void save()}
        confirmLoading={saving}
        destroyOnHidden
        okText="保存"
      >
        <Form key="create" form={form} layout="vertical" preserve={false} initialValues={formInitialValues}>
          {/*
            合同电子版上传放在最前：先传文件 → 自动识别 → 核对下面被预填的字段。
            与「法务工作台」的「新增合同」是同一个组件，规则只有一份。

            识别出的金额、对方单位会自动填进下面的表单（只填用户还没动过的字段），
            低置信度或带冲突的候选**不自动填**，在面板上点「采用」才写入。
          */}
          <Form.Item>
            <ContractAttachmentIntake
              form={form}
              onAttachmentIdsChange={setTempAttachmentIds}
              /*
               * 面板行名与下面表单的字段名对齐：本页叫「对方单位」（总包合同的对方
               * 可能是建设单位，不是供应商），而组件默认沿用「供应商」——
               * 不改就会出现"面板填了供应商、表单上找不到这个框"。
               */
              labelOverrides={{ supplierName: '对方单位', supplierTaxNo: '对方税号' }}
              // 本页的「约定品类」是 Select mode="tags"，值必须是数组（见组件的说明）
              categoriesMode="tags"
              hint="支持 PDF / Word / 图片。上传后会自动提取工程名称、金额、税号、约定品类等，核对后再保存。"
            />
          </Form.Item>

          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item name="contractNo" label="合同编号" extra="留空则由系统生成（HT+日期+随机）">
                <Input className="ilc-mono" placeholder="留空自动生成" />
              </Form.Item>
            </Col>
            <Col xs={24} md={10}>
              <Form.Item name="name" label="合同名称" rules={[{ required: true, message: '请填写合同名称' }]}>
                <Input placeholder="如：泰安市某项目商品混凝土采购合同" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="contractType" label="合同类型" rules={[{ required: true }]}>
                <Select options={Object.entries(CONTRACT_TYPE_LABELS).map(([value, label]) => ({ value, label }))} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={24} md={10}>
              <Form.Item name="supplierName" label="对方单位" rules={[{ required: true, message: '请填写对方单位名称' }]}>
                <Input placeholder="分包商 / 供应商 / 建设单位" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="supplierTaxNo" label="对方税号" extra="18 位统一社会信用代码">
                <Input className="ilc-mono" placeholder="选填" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="counterpartyRole" label="对方身份">
                <Select options={Object.entries(COUNTERPARTY_ROLE_LABELS).map(([value, label]) => ({ value, label }))} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item name="amountYuan" label="合同金额（元，含税）" rules={[{ required: true, message: '请填写合同金额' }]}>
                <InputNumber style={{ width: '100%' }} precision={2} min={0} placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="amountExclTaxYuan" label="不含税金额（元）" extra="选填，用于税额核对">
                <InputNumber style={{ width: '100%' }} precision={2} min={0} placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="taxYuan" label="税额（元）">
                <InputNumber style={{ width: '100%' }} precision={2} min={0} placeholder="0.00" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item name="settlementMethod" label="结算方式">
                <Select
                  allowClear
                  placeholder="选填"
                  options={Object.entries(SETTLEMENT_METHOD_LABELS).map(([value, label]) => ({ value, label }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="settlementStage" label="结算阶段（自由文本）" extra="用于与发票比对">
                <Input placeholder="如：进度款 30%" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="signDates" label="签订日期">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item name="period" label="工期 / 服务期">
                <DatePicker.RangePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="categories" label="约定品类" extra="回车分隔。发票比对靠它判断开票项目是否在约定范围内">
                <Select mode="tags" placeholder="如：商品混凝土 / 钢材" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={1000} />
          </Form.Item>
        </Form>
      </Modal>

      {/* —— 提交审批 —— */}
      <ConfirmDanger
        open={submitTarget !== null}
        title={`提交审批：${submitTarget?.contractNo ?? ''}`}
        description="提交后合同进入审批链（经营部 → 财务 → 法务 → 领导，按类型与金额阈值分流）。审批期间不能修改。"
        level="normal"
        reasonLabel="提交说明（选填，将写入操作日志）"
        minReasonLength={0}
        confirmText="确认提交"
        onConfirm={(reason) => void doSubmit(reason)}
        onCancel={() => setSubmitTarget(null)}
      />

      {/* —— 归档 —— */}
      <ConfirmDanger
        open={archiveTarget !== null}
        title={`归档合同：${archiveTarget?.contractNo ?? ''}`}
        description={
          <span>
            归档表示<strong>这单已经结束</strong>，它会从台账的「已生效」视图里退出。
            <br />
            还有未处理预警的合同不能归档——请先在「合同预警」里确认或忽略它们。
            归档后仍可在档案检索里找到，金额变更需另发补充协议。
          </span>
        }
        level="high"
        reasonLabel="归档说明（必填，将写入操作日志）"
        minReasonLength={4}
        confirmText="确认归档"
        onConfirm={(reason) => void doArchive(reason)}
        onCancel={() => setArchiveTarget(null)}
      />
    </div>
  );
}

export default ContractLedgerPage;
